import React, { useEffect, useRef, useState } from 'react';
import './IePersonCard.sass';
import Beta from '../../../components/common/Beta/Beta';
import { I18n } from 'react-redux-i18n';
import Button from '../../../components/common/Button';
import Loader from '../../../components/common/Loader';
import IeContainerResult from '../IeContainerResult/IeContainerResult';
import IeTemplate from '../IeTemplate/IeTemplate';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  getEmotionalProfile,
  PROFILE_MAP,
} from '../../../utils/emotionalProfiles';
import { getEmotionalProfileValue } from '../../../utils/getEmotionalProfile';
import { computePageCuts } from './computePageCuts';

const IePersonCard = ({
  hasInvitation = false,
  result = undefined,
  playerName,
  onClick,
  disabled,
  personDetails,
  handleProfileLoading,
  lang,
}) => {
  const axes = [
    'self-knowledge',
    'self-regulation',
    'motivation',
    'empathy',
    'social-skills',
  ];

  const [loading, setLoading] = useState(false);
  const [profileType, setProfileType] = useState(null);

  const childRef = useRef(null);

  const handleDownload = async () => {
    if (!childRef.current) return;

    const root = childRef.current;
    setLoading(true);
    handleProfileLoading(true);

    try {
      // 5.1 — Wait for fonts and images to settle before measuring
      await document.fonts.ready;
      await Promise.all(
        Array.from(root.querySelectorAll('img')).map((img) =>
          img.complete && img.naturalHeight > 0
            ? Promise.resolve()
            : new Promise((res) => {
                img.onload = img.onerror = res;
              }),
        ),
      );

      // 5.2 — Collect sorted block-bottom offsets (source px), then scale to canvas px
      const SCALE = 2;
      const rootTop = root.getBoundingClientRect().top;
      const blockElements = Array.from(
        root.querySelectorAll('[data-pdf-block]:not([data-pdf-keep-next])'),
      );
      const blockBottomsSrc = blockElements
        .map((el) => el.getBoundingClientRect().bottom - rootTop)
        .sort((a, b) => a - b);
      const blockBottomsPx = blockBottomsSrc.map((b) => b * SCALE);

      // 5.3 — Capture canvas at fixed scale (width pinned to 794px via inline style)
      const canvas = await html2canvas(root, { scale: SCALE });

      const imgWidthPx = canvas.width; // 1588 at scale:2 with 794px wrapper
      const margin = 10;
      const pdfWidth = 190; // A4 210mm − 2×10mm margins
      const mmPerPx = pdfWidth / imgWidthPx;
      const usablePageHeightPx = Math.round(277 / mmPerPx); // ≈2315 canvas px

      // 5.4 — Build PDF slices using block-boundary page cuts
      const pdf = new jsPDF('p', 'mm', 'a4');
      const cuts = computePageCuts(blockBottomsPx, usablePageHeightPx);

      if (cuts.length === 0) {
        // Fallback: entire canvas fits on one page
        pdf.addImage(
          canvas.toDataURL('image/jpeg', 0.95),
          'JPEG',
          margin,
          margin,
          pdfWidth,
          canvas.height * mmPerPx,
        );
      } else {
        let prev = 0;
        cuts.forEach((cut, idx) => {
          const hPx = cut - prev;
          const slice = document.createElement('canvas');
          slice.width = imgWidthPx;
          slice.height = hPx;
          slice
            .getContext('2d')
            .drawImage(canvas, 0, prev, imgWidthPx, hPx, 0, 0, imgWidthPx, hPx);
          if (idx > 0) pdf.addPage();
          pdf.addImage(
            slice.toDataURL('image/jpeg', 0.95),
            'JPEG',
            margin,
            margin,
            pdfWidth,
            hPx * mmPerPx,
          );
          prev = cut;
        });
      }

      // 5.5 — Save and release loading state
      const fileName = `${personDetails.user.first_name}_${
        personDetails.user.last_name
      }_${I18n.t('common.ie')}.pdf`;

      await pdf.save(fileName, { returnPromise: true });
    } catch (error) {
      console.error('IE report PDF generation failed', error);
    } finally {
      setLoading(false);
      handleProfileLoading(false);
    }
  };

  useEffect(() => {
    if (!result) {
      setProfileType(null);
      return;
    }

    const emotionalProfile = getEmotionalProfile(result.answer);
    const [
      autoconocimiento = 0,
      autorregulacion = 0,
      motivacion = 0,
      empatia = 0,
      habilidadesSociales = 0,
    ] = emotionalProfile?.normalizedValues || [];

    const totalBars =
      autoconocimiento +
      autorregulacion +
      motivacion +
      empatia +
      habilidadesSociales;

    const profile = getEmotionalProfileValue(totalBars, {
      autoconocimiento,
      autorregulacion,
      motivacion,
      empatia,
      habilidadesSociales,
    });

    setProfileType(profile ? PROFILE_MAP[profile] : null);
  }, [result]);
  return (
    <>
      <div className="ie-person-card-cointainer">
        <div className="ie-header">
          <span className="material-symbols-filled">diagnosis</span>
          <h3>{I18n.t('campaigns.ie.title')}</h3>
          <Beta />
          {loading ? (
            <Loader mini />
          ) : (
            <Button
              className="secondary"
              text={I18n.t('persons.downloadReport')}
              iconClass="download"
              iconButton
              disabled={!result ? true : false}
              onClick={handleDownload}
            />
          )}
        </div>

        {!hasInvitation && !result && (
          <div className="without-invitation invitation">
            <p
              dangerouslySetInnerHTML={{
                __html: I18n.t('persons.IE.text-without-invitation', {
                  name: playerName,
                }),
              }}
            />

            <Button
              className={'secondary'}
              iconButton={true}
              iconFilled={true}
              iconClass={'sports_esports'}
              text={I18n.t('persons.IE.button')}
              disabled={disabled}
              onClick={onClick}
            />
          </div>
        )}
        {hasInvitation && !result && (
          <div className="with-invitation invitation">
            <p
              dangerouslySetInnerHTML={{
                __html: I18n.t('persons.IE.text-with-invitation', {
                  name: playerName,
                }),
              }}
            />
          </div>
        )}
        {result && (
          <div>
            <div
              style={{ position: 'absolute', top: '-9999px', width: '794px' }}
              ref={childRef}
            >
              <IeTemplate
                result={result.answer.split(',').flatMap((total, i) => ({
                  value: parseInt(total),
                  axis: axes[i],
                }))}
                personDetails={personDetails}
                playerName={playerName}
                lang={lang}
                emotionalProfile={profileType}
              />
            </div>
            <IeContainerResult
              result={result.answer.split(',').flatMap((total, i) => ({
                value: parseInt(total),
                axis: axes[i],
              }))}
              playerName={playerName}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default IePersonCard;
